import { useState, useRef, useEffect, useCallback } from 'react';
import Navbar from '../GeneralComponents/Navbar';
import SideNav from '../GeneralComponents/SideNav';
import BackButton from '../GeneralComponents/BackButton';
import { toast } from 'react-hot-toast';
import { getServerError } from '../../helper/misc';
import auth from '../../api/auth';
import { HiOutlineMail } from 'react-icons/hi';

const OTP_LENGTH = 6;
const COOLDOWN_SECONDS = 60;

function maskEmail(email) {
  if (!email) return '';
  const [local, domain] = email.split('@');
  if (local.length <= 2) return `${local[0]}***@${domain}`;
  return `${local[0]}${local[1]}***@${domain}`;
}

function VerifyOTP({ email, signupPayload, prevStep, nextStep }) {
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''));
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(COOLDOWN_SECONDS);
  const inputsRef = useRef([]);

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((c) => c - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  // Focus first input on mount
  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  const handleChange = (index, value) => {
    // Only allow single digits
    if (value && !/^\d$/.test(value)) return;

    const updated = [...otp];
    updated[index] = value;
    setOtp(updated);

    // Auto-advance to next input
    if (value && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    const updated = [...otp];
    for (let i = 0; i < pasted.length; i++) {
      updated[i] = pasted[i];
    }
    setOtp(updated);
    // Focus the input after the last pasted character
    const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1);
    inputsRef.current[focusIndex]?.focus();
  };

  const code = otp.join('');
  const isComplete = code.length === OTP_LENGTH;

  const handleVerify = useCallback(async () => {
    if (!isComplete || verifying) return;

    setVerifying(true);
    try {
      // Step 1: Verify the OTP
      await auth.verifyOtp({ email, code });

      // Step 2: Register the account
      await auth.register(signupPayload);

      setVerifying(false);
      nextStep();
    } catch (e) {
      toast.error(getServerError(e));
      setVerifying(false);
      // Clear OTP inputs on error so user can retry
      setOtp(Array(OTP_LENGTH).fill(''));
      inputsRef.current[0]?.focus();
    }
  }, [isComplete, verifying, email, code, signupPayload, nextStep]);

  const handleResend = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true);
    try {
      await auth.requestVerification({ email });
      toast.success('A new code has been sent to your email');
      setCooldown(COOLDOWN_SECONDS);
      setOtp(Array(OTP_LENGTH).fill(''));
      inputsRef.current[0]?.focus();
    } catch (e) {
      toast.error(getServerError(e));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 min-h-screen bg-gray-50">
      <SideNav className="!static" />

      <div className="col-span-1 lg:col-span-3 flex flex-col">
        <Navbar />

        <div className="flex-1 flex flex-col items-center justify-center px-4 py-12">
          {/* Card */}
          <div className="w-full max-w-md">
            {/* Page header */}
            <div className="mb-8">
              <div className="flex items-center gap-4">
                <BackButton onClick={prevStep} />
                <div>
                  <p className="text-xs font-semibold tracking-widest text-pw-blue uppercase">
                    Email Verification
                  </p>
                  <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 leading-tight">
                    Check your email
                  </h1>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg shadow-gray-200/60 border border-gray-100 p-8">
              {/* Email indicator */}
              <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3 mb-6">
                <HiOutlineMail className="text-pw-blue shrink-0" size={20} />
                <div>
                  <p className="text-xs text-gray-400">Code sent to</p>
                  <p className="text-sm font-semibold text-gray-700">
                    {maskEmail(email)}
                  </p>
                </div>
              </div>

              <p className="text-sm text-gray-500 mb-6">
                Enter the 6-digit code we sent to your email. The code expires in 15 minutes.
              </p>

              {/* OTP Inputs */}
              <div className="flex justify-center gap-2 sm:gap-3 mb-6">
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => (inputsRef.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleChange(i, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    onPaste={i === 0 ? handlePaste : undefined}
                    aria-label={`Digit ${i + 1}`}
                    className={`w-12 h-14 sm:w-14 sm:h-16 text-center text-xl font-bold border-2 rounded-xl bg-white transition-all duration-200 outline-none
                      ${
                        digit
                          ? 'border-pw-blue shadow-sm shadow-[#109CF1]/10'
                          : 'border-gray-200 hover:border-gray-300'
                      }
                      focus:border-pw-blue focus:shadow-md focus:shadow-[#109CF1]/10`}
                  />
                ))}
              </div>

              {/* Verify button */}
              <button
                type="button"
                id="verify-otp-btn"
                onClick={handleVerify}
                disabled={!isComplete || verifying}
                className="w-full py-3.5 bg-pw-blue hover:bg-[#0d8fd9] active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-sm rounded-3xl transition-all duration-200 shadow-md shadow-[#109CF1]/30 flex items-center justify-center gap-2">
                {verifying ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Verifying…
                  </>
                ) : (
                  'Verify & Create Account'
                )}
              </button>

              {/* Resend */}
              <div className="text-center mt-6">
                <p className="text-sm text-gray-500">
                  Didn't receive a code?{' '}
                  {cooldown > 0 ? (
                    <span className="text-gray-400">
                      Resend in {cooldown}s
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResend}
                      disabled={resending}
                      className="text-pw-blue font-semibold hover:underline disabled:opacity-50">
                      {resending ? 'Sending…' : 'Resend code'}
                    </button>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VerifyOTP;
